import { drizzle } from 'drizzle-orm/d1'
import * as schema from './schema/index.js'

// Mock database for development
class MockDatabase {
  private data: Record<string, any[]> = {
    users: [],
    admin_users: [],
    trainings: [],
    attendance: [],
    voucher_logs: [],
    bootcamps: [],
    subscriptions: []
  }

  constructor() {
    // Seed with initial data for development
    this.seedDevelopmentData()
  }

  private seedDevelopmentData() {
    // Add admin user
    this.data.admin_users = [{
      id: 1,
      username: 'admin',
      password_hash: '$2a$10$92IXUNpkjO0rOQ5byMi.Ye4oKoEa3Ro9llC/.og/at2.uheWG/igi', // password
      created_at: new Date().toISOString()
    }]

    // Add sample users
    this.data.users = [
      {
        id: 1,
        namn: 'John Doe',
        epost: 'john@example.com',
        telefon: '+46701234567',
        vouchers: 5,
        completed_trainings: 2,
        created_at: new Date().toISOString()
      },
      {
        id: 2,
        namn: 'Jane Smith',
        epost: 'jane@example.com',
        telefon: '+46701234568',
        vouchers: 3,
        completed_trainings: 1,
        created_at: new Date().toISOString()
      }
    ]

    // Add sample trainings
    this.data.trainings = [
      {
        id: 1,
        titel: 'Introduction to React',
        beskrivning: 'Learn the basics of React development',
        datum: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        location: 'Stockholm',
        max_platser: 20,
        bootcamp: 0,
        bootcamp_id: null,
        free_training: 0,
        allow_non_bootcamp_join: 1,
        created_at: new Date().toISOString(),
        anmalda: 0,
        bootcamp_name: null
      },
      {
        id: 2,
        titel: 'Advanced TypeScript',
        beskrivning: 'Deep dive into TypeScript features',
        datum: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        location: 'Gothenburg',
        max_platser: 15,
        bootcamp: 1,
        bootcamp_id: null,
        free_training: 0,
        allow_non_bootcamp_join: 0,
        created_at: new Date().toISOString(),
        anmalda: 0,
        bootcamp_name: 'Full Stack Bootcamp'
      }
    ]
  }

  select() {
    return {
      from: (table: any) => ({
        where: (_condition: any) => ({
          limit: (_count: number) => ({
            offset: (_offset: number) => Promise.resolve(this.data[table.name] || [])
          })
        }),
        orderBy: (_orderBy: any) => ({
          limit: (_count: number) => ({
            offset: (_offset: number) => Promise.resolve(this.data[table.name] || [])
          })
        }),
        limit: (_count: number) => ({
          offset: (_offset: number) => Promise.resolve(this.data[table.name] || [])
        })
      })
    }
  }

  insert(table: any) {
    return {
      values: (data: any) => ({
        returning: () => {
          const newId = Date.now()
          const newRecord = { id: newId, ...data }
          if (!this.data[table.name]) {
            this.data[table.name] = []
          }
          this.data[table.name].push(newRecord)
          return Promise.resolve([newRecord])
        }
      })
    }
  }

  update(table: any) {
    return {
      set: (data: any) => ({
        where: (_condition: any) => ({
          returning: () => {
            // Simple mock update - in real implementation this would filter by condition
            if (this.data[table.name] && this.data[table.name].length > 0) {
              const updatedRecord = { ...this.data[table.name][0], ...data }
              this.data[table.name][0] = updatedRecord
              return Promise.resolve([updatedRecord])
            }
            return Promise.resolve([])
          }
        })
      })
    }
  }

  delete(_table: any) {
    return {
      where: (_condition: any) => Promise.resolve()
    }
  }
}

// For Cloudflare Workers/Pages (production)
export function getDb(d1?: D1Database) {
  if (typeof d1 !== 'undefined') {
    return drizzle(d1, { schema })
  }
  
  // For browser environment, we need to access D1 through the global scope
  // This will be available when running in Cloudflare Pages
  if (typeof globalThis !== 'undefined' && 'DB' in globalThis) {
    const d1Instance = (globalThis as any).DB as D1Database
    if (d1Instance) {
      return drizzle(d1Instance, { schema })
    }
  }
  
  // Fallback for development environment
  console.warn('D1 database not available, using mock database for development')
  return new MockDatabase() as any
}

// For local development (fallback)
export function getLocalDb() {
  console.warn('Using local database - this should only happen in development')
  return new MockDatabase() as any
}

// Migration helper
export async function migrateDb(d1: D1Database) {
  const db = getDb(d1)
  console.log('Database connection established')
  return db
}

// Helper to check if we're in a Cloudflare environment
export function isCloudflareEnvironment(): boolean {
  return typeof globalThis !== 'undefined' && 'DB' in globalThis
}